/**
 * CDM-specific formatting utilities for DataTable rendering.
 *
 * Provides:
 * - Pc (collision probability) in scientific notation with risk-based color coding
 * - Miss distance formatting
 * - Relative TCA display ("in 2d 3h" or "2d ago")
 * - Emergency reportable detection
 * - CDM column ordering and metadata
 *
 * Color coding follows PRINCIPLES.md:
 *   red   = danger  (Pc > 1e-4)
 *   amber = warning (Pc > 1e-5)
 *   green = nominal (Pc <= 1e-5)
 */

// Risk level thresholds per PRINCIPLES.md
export const PC_DANGER_THRESHOLD = 1e-4
export const PC_WARNING_THRESHOLD = 1e-5

export type RiskLevel = 'danger' | 'warning' | 'nominal' | 'unknown'

/**
 * Coerce an unknown value to a finite number, or null if it isn't one.
 *
 * Guards against `Number()`'s coercion of "empty" inputs to 0:
 * `Number('') === 0`, `Number(' ') === 0`, `Number(null) === 0`,
 * `Number([]) === 0`. For safety-critical fields (collision probability) a
 * blank/missing value must read as "unknown", never silently as 0 ("nominal").
 */
function toFiniteNumber(value: unknown): number | null {
  if (value == null) return null
  // Accept only numbers and numeric strings. Reject objects/arrays/booleans
  // outright: Number([]) === 0 and Number(false) === 0 would otherwise slip
  // through as a real 0 ("nominal") for a non-numeric field.
  if (typeof value !== 'number' && typeof value !== 'string') return null
  if (typeof value === 'string' && value.trim() === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

/**
 * Determine risk level from collision probability value.
 */
export function getRiskLevel(pc: unknown): RiskLevel {
  const num = toFiniteNumber(pc)
  if (num === null) return 'unknown'
  if (num > PC_DANGER_THRESHOLD) return 'danger'
  if (num > PC_WARNING_THRESHOLD) return 'warning'
  return 'nominal'
}

/**
 * CSS color for a risk level.
 * red=danger, amber=warning, green=nominal, dim=unknown
 */
export function riskColor(level: RiskLevel): string {
  switch (level) {
    case 'danger':
      return '#ff4444'
    case 'warning':
      return '#ffaa00'
    case 'nominal':
      return '#44cc44'
    case 'unknown':
      return '#888888'
  }
}

/**
 * Format collision probability as scientific notation (e.g., "2.67e-02").
 */
export function formatPc(value: unknown): string {
  if (value == null) return 'N/A'
  const num = toFiniteNumber(value)
  if (num === null) return String(value)
  return num.toExponential(2)
}

/**
 * Format miss distance in meters with unit suffix.
 * Values >= 1000m shown as km.
 */
export function formatMissDistance(value: unknown): string {
  if (value == null) return '-'
  const num = toFiniteNumber(value)
  if (num === null) return String(value)
  if (num === 0) return '0 m'
  if (Math.abs(num) >= 1000) {
    return `${(num / 1000).toFixed(2)} km`
  }
  return `${num.toFixed(1)} m`
}

/**
 * Format TCA as relative time ("in 2d 3h" or "2d 3h ago").
 */
export function formatRelativeTca(value: unknown): string {
  if (!value) return '-'
  try {
    const date = new Date(String(value))
    if (isNaN(date.getTime())) return String(value)

    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const absDiffMs = Math.abs(diffMs)
    const isFuture = diffMs > 0

    const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24))
    const hours = Math.floor((absDiffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60))

    let parts: string[] = []
    if (days > 0) parts.push(`${days}d`)
    if (hours > 0) parts.push(`${hours}h`)
    if (days === 0 && minutes > 0) parts.push(`${minutes}m`)
    if (parts.length === 0) parts.push('now')

    const timeStr = parts.join(' ')
    if (timeStr === 'now') return 'now'
    return isFuture ? `in ${timeStr}` : `${timeStr} ago`
  } catch {
    return String(value)
  }
}

/**
 * Detect if a row is emergency reportable.
 */
export function isEmergencyReportable(row: Record<string, unknown>): boolean {
  return (
    row.emergency_reportable === true ||
    row.EMERGENCY_REPORTABLE === true ||
    row.emergency === true
  )
}

/**
 * Preferred column order for CDM tables.
 * Columns not in this list appear after these, in their original order.
 */
const CDM_COLUMN_ORDER = [
  'collision_probability',
  'miss_distance',
  'miss_distance_m',
  'miss_distance_km',
  'tca',
  'sat1_name',
  'sat_1_name',
  'sat1',
  'sat2_name',
  'sat_2_name',
  'sat2',
  'sat1_norad_id',
  'sat1_norad_cat_id',
  'sat2_norad_id',
  'sat2_norad_cat_id',
  'emergency_reportable',
  'emergency',
  'cdm_id',
  'probability',
]

/**
 * Columns to hide from CDM tables (internal IDs, redundant fields).
 */
const CDM_HIDDEN_COLUMNS = new Set([
  'id',
  'created_at',
  'updated_at',
  'insertion_order',
])

/**
 * Sort column keys for CDM display: preferred columns first, then the rest.
 * Filters out hidden columns.
 */
export function sortCdmColumns(keys: string[]): string[] {
  const visible = keys.filter((k) => !CDM_HIDDEN_COLUMNS.has(k))
  const ordered: string[] = []
  const remaining = new Set(visible)

  for (const col of CDM_COLUMN_ORDER) {
    if (remaining.has(col)) {
      ordered.push(col)
      remaining.delete(col)
    }
  }

  // Append remaining columns in original order
  for (const key of visible) {
    if (remaining.has(key)) {
      ordered.push(key)
    }
  }

  return ordered
}

/**
 * Check if a column key represents a collision probability field.
 */
export function isPcColumn(key: string): boolean {
  return (
    key === 'collision_probability' ||
    key === 'COLLISION_PROBABILITY' ||
    key === 'pc' ||
    key === 'probability'
  )
}

/**
 * Check if a column key represents a miss distance field.
 */
export function isMissDistanceColumn(key: string): boolean {
  return (
    key.includes('miss_distance') ||
    key.includes('MISS_DISTANCE') ||
    key === 'miss_dist'
  )
}

/**
 * Check if a column key represents a TCA field.
 */
export function isTcaColumn(key: string): boolean {
  return key === 'tca' || key === 'TCA'
}

/**
 * Extract CDM results array from an artifact data wrapper.
 *
 * The backend wraps CDM query results in:
 *   { success: true, count: N, message: "...", results: [...] }
 *
 * This function extracts the `results` array if present.
 * Returns null if the data is not a CDM wrapper.
 */
export function extractCdmResults(
  data: Record<string, unknown> | unknown[] | null
): Record<string, unknown>[] | null {
  if (!data) return null

  // Already an array -- return directly
  if (Array.isArray(data)) {
    return data.length > 0 ? (data as Record<string, unknown>[]) : null
  }

  // Check for wrapper structure: { results: [...] }
  const obj = data as Record<string, unknown>
  if (Array.isArray(obj.results) && obj.results.length > 0) {
    return obj.results as Record<string, unknown>[]
  }

  return null
}

/**
 * Detect if an artifact contains CDM tabular data.
 */
export function isCdmTableArtifact(artifact: {
  type: string
  title: string
  data: unknown
}): boolean {
  if (artifact.type !== 'data') return false

  const data = artifact.data as Record<string, unknown> | null
  if (!data) return false

  // Check for display_hint
  if (data.display_hint === 'table') return true

  // Check for results array with CDM-like fields
  const results = extractCdmResults(data)
  if (!results || results.length === 0) return false

  const firstRow = results[0]
  return (
    'collision_probability' in firstRow ||
    'tca' in firstRow ||
    'cdm_id' in firstRow ||
    'probability' in firstRow ||
    'miss_distance' in firstRow
  )
}
